/**
 * Config schemas for dashboards and widgets.
 *
 * Everything here validates hand-written (and AI-written) YAML, so error
 * messages are part of the product: a bad config must produce a sentence that
 * tells you which file and which key to fix. See `formatIssues`.
 */

import { z } from 'zod';

// ─── Grid + layout ──────────────────────────────────────────────────────────

export const GridSchema = z
  .object({
    columns: z.number().int().positive().max(48).default(12),
    rowHeight: z.number().positive().max(1000).default(80),
    gap: z.number().nonnegative().max(200).default(12),
    maxWidth: z.number().positive().optional(),
  })
  .default({});

export type Grid = z.infer<typeof GridSchema>;

export const PlacementSchema = z.object({
  /** Widget id — resolves to `widgets/<id>.md`, or an inline widget of the same id. */
  widget: z.string().min(1),
  x: z.number().int().min(0),
  y: z.number().int().min(0),
  w: z.number().int().positive(),
  h: z.number().int().positive(),
});

export type Placement = z.infer<typeof PlacementSchema>;

export const DashboardSchema = z.object({
  title: z.string().optional(),
  theme: z.string().default('midnight'),
  grid: GridSchema,
  layout: z.array(PlacementSchema).default([]),
});

export type Dashboard = z.infer<typeof DashboardSchema>;

// ─── Widgets ────────────────────────────────────────────────────────────────

/**
 * Span used when a widget lands on Home via `showOnHome` rather than an
 * explicit layout entry. Absent means full width at {@link DEFAULT_HOME_HEIGHT}.
 */
export const HomeSpanSchema = z.object({
  w: z.number().int().positive(),
  h: z.number().int().positive(),
});

export type HomeSpan = z.infer<typeof HomeSpanSchema>;

/** Fields every widget config carries. Individual widgets extend this. */
export const WidgetBaseSchema = z.object({
  id: z.string().min(1),
  type: z.string().min(1),
  title: z.string().optional(),
  /** Hide the card chrome (header + border) — for banners and other bleed-to-edge widgets. */
  bare: z.boolean().default(false),
  /**
   * Surface this widget on the home dashboard without editing its layout.
   *
   * Appended below everything Home places explicitly, in alphabetical order by
   * widget id. An explicit entry in `dashboards/main.md` always wins — listing a
   * widget there and setting this flag places it once, where the layout says.
   *
   * The flag trades control for convenience: it cannot express position, which
   * is exactly why the explicit layout stays the primary mechanism.
   */
  showOnHome: z.boolean().default(false),
  /** Size for the `showOnHome` placement only. Ignored when placed explicitly. */
  homeSpan: HomeSpanSchema.optional(),
});

export type WidgetBase = z.infer<typeof WidgetBaseSchema>;

/** Rows a `showOnHome` widget occupies when it doesn't specify `homeSpan`. */
export const DEFAULT_HOME_HEIGHT = 3;

/** Just enough to route a config to the right schema before full validation. */
export const WidgetProbeSchema = z.object({
  id: z.string().min(1).optional(),
  type: z.string().min(1),
});

// ─── Error reporting ────────────────────────────────────────────────────────

/**
 * Turn a ZodError into something a human can act on without knowing zod.
 * Rendered directly inside the failing widget's tile alongside its file path.
 */
export function formatIssues(error: z.ZodError): string {
  return error.issues
    .map((issue) => {
      const path = issue.path.join('.');
      return path ? `${path}: ${issue.message}` : issue.message;
    })
    .join('; ');
}

/** Does the platform's tz database know this identifier at all? */
export function isValidTimeZone(tz: string): boolean {
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: tz });
    return true;
  } catch {
    return false;
  }
}

/**
 * Legacy fixed-offset identifiers that the tz database still accepts.
 *
 * These are a trap rather than a typo: `Intl` happily accepts `"EST"`, but it
 * is pinned to UTC-5 year-round and never shifts to EDT. A clock configured
 * with it looks right in January and runs an hour slow from March to November.
 * Since the whole point of a dual-timezone clock is being correct at a glance,
 * we reject them by name and say what to use instead.
 */
const FIXED_OFFSET_ALIASES = new Map<string, string>([
  ['EST', 'America/New_York'],
  ['EST5EDT', 'America/New_York'],
  ['CST6CDT', 'America/Chicago'],
  ['MST', 'America/Denver'],
  ['MST7MDT', 'America/Denver'],
  ['PST8PDT', 'America/Los_Angeles'],
  ['HST', 'Pacific/Honolulu'],
  ['GMT', 'UTC'],
  ['Greenwich', 'UTC'],
  ['Universal', 'UTC'],
  ['Zulu', 'UTC'],
]);

/** Fixed offsets that are unambiguous and safe to use deliberately. */
const ALLOWED_FIXED = new Set(['UTC']);

/**
 * Returns a human-actionable problem with a time zone, or null if it's fine.
 * Stricter than {@link isValidTimeZone} on purpose — see above.
 */
export function describeTimeZoneProblem(tz: string): string | null {
  if (!isValidTimeZone(tz)) {
    return `"${tz}" is not a known IANA time zone (try "America/New_York")`;
  }

  if (ALLOWED_FIXED.has(tz)) return null;

  const suggestion = FIXED_OFFSET_ALIASES.get(tz);
  if (suggestion) {
    return `"${tz}" is a fixed-offset zone that never observes daylight saving — use "${suggestion}" so the clock follows DST`;
  }

  if (tz.startsWith('Etc/')) {
    return `"${tz}" is a fixed-offset zone that never observes daylight saving — use a region zone like "America/New_York"`;
  }

  if (!tz.includes('/')) {
    return `"${tz}" is an abbreviation, not a region zone — use a "Region/City" identifier like "America/New_York"`;
  }

  return null;
}
